import { create } from 'zustand';
import { type DistanceUnit, parseDistanceUnit, toggleUnit } from '../models/types';

/** Keys under which the device remembers the player's choices. */
export const DISTANCE_UNIT_KEY = 'ethrhunt.distanceUnit';
export const MULTI_PART_TIP_KEY = 'ethrhunt.multiPartTipSeen';
/** Active play time on this device, in seconds. */
export const PLAY_SECONDS_KEY = 'ethrhunt.playSeconds';
/** The highest feedback milestone (10 or 60) already offered on this device; absent = none. */
export const FEEDBACK_DONE_KEY = 'ethrhunt.feedbackMilestoneDone';

// Reading `localStorage` itself can throw when storage is disabled.
function storage(): Storage | null {
  try {
    return window.localStorage;
  } catch {
    return null;
  }
}

export function read(key: string): string | null {
  try {
    return storage()?.getItem(key) ?? null;
  } catch {
    return null;
  }
}

/** A failed write never surfaces: the choice still holds for this session in memory. */
export function write(key: string, value: string) {
  try {
    storage()?.setItem(key, value);
  } catch {
    /* storage full or blocked */
  }
}

interface PreferencesState {
  distanceUnit: DistanceUnit;
  /** The player tapped "Got it" on the multi-part helper card. */
  multiPartTipSeen: boolean;
  toggleDistanceUnit: () => void;
  markMultiPartTipSeen: () => void;
}

const load = () => ({
  distanceUnit: parseDistanceUnit(read(DISTANCE_UNIT_KEY)),
  multiPartTipSeen: read(MULTI_PART_TIP_KEY) === '1',
});

export const usePreferences = create<PreferencesState>((set, get) => ({
  ...load(),
  toggleDistanceUnit: () => {
    const next = toggleUnit(get().distanceUnit);
    set({ distanceUnit: next });
    write(DISTANCE_UNIT_KEY, next);
  },
  markMultiPartTipSeen: () => {
    set({ multiPartTipSeen: true });
    write(MULTI_PART_TIP_KEY, '1');
  },
}));

/** Tests: re-read the device store, as a page reload would. */
export function resetPreferences() {
  usePreferences.setState(load());
}
